/**
 * Algolia PLP pricing/badges — must match Search Spring source (do not invent rules here).
 *
 * - Price calculation: Search Spring/src/shared/shared/plugins/shared.js (afterStore → result.custom)
 * - Price HTML: Search Spring/src/shared/pricing/Pricing.jsx
 * - Tag discounts: variant metafield pricing_tag + shop.metafields.custom.tag_based_discount_config
 *   (same as snippets/custom-price.liquid; SS uses attributes.variants JSON from the feed)
 *
 * Algolia-only adapters (feed already provides correct core.price; Algolia index does not):
 * - listPriceFromHit() → SS mappings.core.price (prefer variants_min_price / Shopify price_min)
 * - hit._plpVariantPricingById from Liquid / pricing-bridge (Shopify .js has no metafields)
 * - Inject bridge metafields onto variants, then pick best visible member/trade (shared.js) —
 *   do NOT pin to selected_or_first_available_variant
 * - SS uses member_pricing / trade_pricing only (no member_promo_price / trade_promo_price)
 * - Optional ctx.plpPricingMode === 'lowestList' is legacy; prefer default shared.js path
 *
 * ss_price_member is requested in SS index fields but is not used in shared.js (dead destructuring).
 */
(function (global) {
  'use strict';

  function calculateFloat(num) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
  }

  /** Liquid sometimes emitted money metafields as cents; normalize against PLP list price. */
  function normalizePlpMetafieldMoney(n, referenceDollars) {
    if (n == null || !(n > 0)) return null;
    var ref = referenceDollars != null ? parseFloat(referenceDollars) : NaN;
    if (!isNaN(ref) && ref > 0 && n > ref * 1.5) {
      var fromCents = calculateFloat(n / 100);
      if (fromCents > 0 && fromCents <= ref * 1.05) return fromCents;
    }
    return n;
  }

  function parseVariantsFromHit(hit) {
    var raw = hit.variants || (hit.attributes && hit.attributes.variants);
    if (!raw) return [];
    if (Array.isArray(raw)) {
      if (raw.length && typeof raw[0] === 'string') {
        try {
          return JSON.parse(raw[0]);
        } catch (e) {
          return [];
        }
      }
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (e2) {
        return [];
      }
    }
    return [];
  }

  /** PLP list price — Algolia `price` is often not the collection minimum. */
  function listPriceFromHit(hit) {
    if (hit._shopify && hit._shopify.price_min != null) {
      return hit._shopify.price_min / 100;
    }
    if (hit.variants_min_price != null && hit.variants_min_price !== '') {
      var minP = parseFloat(hit.variants_min_price);
      if (!isNaN(minP) && minP > 0) return minP;
    }
    var raw = hit.price != null ? parseFloat(hit.price) : NaN;
    if (!isNaN(raw) && raw > 0) {
      if (raw > 10000) return raw / 100;
      return raw;
    }
    return 0;
  }

  function compareAtFromHit(hit) {
    if (hit._shopify && hit._shopify.compare_at_price_min != null && hit._shopify.compare_at_price_min > 0) {
      return hit._shopify.compare_at_price_min / 100;
    }
    var c = hit.compare_at_price != null ? parseFloat(hit.compare_at_price) : NaN;
    if (!isNaN(c) && c > 0) {
      if (c > 10000) return c / 100;
      return c;
    }
    return null;
  }

  function findShopifyVariant(product, variantId) {
    if (!product || !product.variants || variantId == null) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
    }
    return null;
  }

  function coreFromShopifyVariant(v) {
    if (!v) return null;
    var list = v.price / 100;
    var compare = v.compare_at_price ? v.compare_at_price / 100 : list;
    var msrp = compare > list ? compare : list;
    return { price: list, msrp: msrp, fullPrice: msrp };
  }

  function shopifyVariantsAsSnapVariants(product) {
    if (!product || !product.variants) return [];
    return product.variants.map(function (v) {
      return {
        id: v.id,
        price: (v.price / 100).toFixed(2),
        compare_at_price: v.compare_at_price ? (v.compare_at_price / 100).toFixed(2) : null,
        metafields: [],
        mfield_custom_hidden_on_web: 'false',
      };
    });
  }

  function getMfieldRaw(v, key) {
    var vMfields = v.metafields || [];
    for (var i = 0; i < vMfields.length; i++) {
      if (vMfields[i].key === key) return vMfields[i].value;
    }
    return v['mfield_custom_' + key] || null;
  }

  function getMfieldMoney(v, key) {
    var val = getMfieldRaw(v, key);
    if (val == null || val === '') return null;
    if (typeof val === 'number') return val > 0 ? val : null;
    try {
      var parsed = JSON.parse(val);
      if (typeof parsed === 'number') return parsed > 0 ? parsed : null;
      if (parsed && parsed.amount != null) {
        var amount = parseFloat(parsed.amount);
        return isNaN(amount) || amount <= 0 ? null : amount;
      }
    } catch (e) {}
    var n = parseFloat(val);
    return isNaN(n) || n <= 0 ? null : n;
  }

  /** SS index.js shopperHasTag — case-insensitive member / trade. */
  function normalizeSiteFlags(site) {
    site = site || {};
    var tags = site.customerTags || [];
    var loggedInMember = !!site.loggedInMember;
    var loggedInTrade = !!site.loggedInTrade;
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || '').toLowerCase();
      if (t === 'member') loggedInMember = true;
      if (t === 'trade') loggedInTrade = true;
    }
    return {
      loggedInMember: loggedInMember,
      loggedInTrade: loggedInTrade,
      customerTags: tags,
    };
  }

  /**
   * Shopify product.js has no metafields. Copy Liquid/bridge rows onto variants so
   * shared.js bestVisibleMember / pricing_tag sweeps work.
   */
  function applyBridgeMetafieldsToVariants(hit, variants) {
    var byId = hit && hit._plpVariantPricingById;
    if (!byId || !variants || !variants.length) return;
    variants.forEach(function (v) {
      if (!v || v.id == null) return;
      var row = byId[String(v.id)];
      if (!row) return;
      var refDollars = parseFloat(v.price);
      if (isNaN(refDollars) || refDollars <= 0) refDollars = null;
      if (row.pricingTag != null && row.pricingTag !== '') {
        v.mfield_custom_pricing_tag =
          typeof row.pricingTag === 'string' ? row.pricingTag : JSON.stringify(row.pricingTag);
      }
      if (row.memberPricing != null && row.memberPricing !== '') {
        var mem = normalizePlpMetafieldMoney(parseFloat(row.memberPricing), refDollars);
        if (mem == null || !(mem > 0)) mem = parseFloat(row.memberPricing);
        if (mem != null && !isNaN(mem) && mem > 0) {
          v.mfield_custom_member_pricing = String(mem);
        }
      }
      if (row.tradePricing != null && row.tradePricing !== '') {
        var tr = normalizePlpMetafieldMoney(parseFloat(row.tradePricing), refDollars);
        if (tr == null || !(tr > 0)) tr = parseFloat(row.tradePricing);
        if (tr != null && !isNaN(tr) && tr > 0) {
          v.mfield_custom_trade_pricing = String(tr);
        }
      }
      if (row.hiddenOnWeb === true || row.hiddenOnWeb === 'true' || row.hiddenOnWeb === 1) {
        v.mfield_custom_hidden_on_web = 'true';
      }
    });
  }

  /* Campaign tags only — not megaexpressliving / collection merch tags */
  function isExpressCampaignTag(tag) {
    var t = String(tag || '').toLowerCase();
    if (t === 'express-sale') return true;
    if (/^megaexpress\d/.test(t)) return true;
    if (t === 'megaexpress-sale' || t === 'mega-express-sale') return true;
    if (/express\s*40|express40/.test(t)) return true;
    if (/express\s*30|express30/.test(t)) return true;
    if (/express\s*20|express20/.test(t)) return true;
    return false;
  }

  function isExpressProductTags(productTags) {
    for (var i = 0; i < productTags.length; i++) {
      if (isExpressCampaignTag(productTags[i])) return true;
    }
    return false;
  }

  function inferPricingTagFromTags(tags) {
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || '');
      if (/express\s*40|express40/i.test(t)) return 'Express40';
      if (/express\s*20|express20/i.test(t)) return 'Express20';
      if (/express\s*30|express30/i.test(t)) return 'Express30';
    }
    for (var j = 0; j < tags.length; j++) {
      if (isExpressCampaignTag(tags[j])) return 'Express30';
    }
    return null;
  }

  function readFeedMemberPrice(hit) {
    var raw =
      hit.ss_price_member != null && hit.ss_price_member !== ''
        ? hit.ss_price_member
        : hit.attributes && hit.attributes.ss_price_member;
    if (raw == null || raw === '') return null;
    var n = parseFloat(raw);
    return isNaN(n) || n <= 0 ? null : n;
  }

  function loyaltyMemberPrice(amount, discounts) {
    if (amount == null || isNaN(amount)) return null;
    var pct = parseFloat(discounts && discounts.member != null && discounts.member !== '' ? discounts.member : 15);
    if (isNaN(pct) || pct <= 0) return null;
    return calculateFloat((Number(amount) * (100 - pct)) / 100);
  }

  function tagDiscountForPricingTag(tagCfg, site, pricingTagName, vFullPrice) {
    if (!tagCfg || !tagCfg.enabled || !tagCfg.rules) return null;
    var activeRule = null;
    for (var ri = 0; ri < tagCfg.rules.length; ri++) {
      var rule = tagCfg.rules[ri];
      if (!rule.enabled) continue;
      if (String(rule.pricingTag).toLowerCase() === String(pricingTagName).toLowerCase()) {
        activeRule = rule;
        break;
      }
    }
    if (!activeRule) return null;

    function getPriceForRule(customerTag) {
      for (var rj = 0; rj < tagCfg.rules.length; rj++) {
        var r = tagCfg.rules[rj];
        if (!r.enabled || r.pricingTag !== activeRule.pricingTag) continue;
        if (String(r.customerTag || '') !== String(customerTag || '')) continue;
        if (r.excludedCustomerTags && r.excludedCustomerTags.length) {
          for (var ex = 0; ex < r.excludedCustomerTags.length; ex++) {
            if ((site.customerTags || []).indexOf(r.excludedCustomerTags[ex]) !== -1) return null;
          }
        }
        if (r.discountType === 'percentage') {
          return calculateFloat((vFullPrice * (100 - Number(r.discountValue))) / 100);
        }
      }
      return null;
    }

    var standard = getPriceForRule('');
    var member = getPriceForRule('member');
    var trade = getPriceForRule('trade');
    if (!standard && !member && !trade) return null;
    return {
      standard: standard,
      member: member,
      trade: trade,
      label: activeRule.label,
      variantId: null,
      fullPrice: vFullPrice,
    };
  }

  function tagBasedFromVariantPricingRows(variantPricing, site) {
    if (!variantPricing || !variantPricing.length) return null;
    var priceKey = site.loggedInTrade ? 'trade' : 'member';
    var sorted = variantPricing.slice().sort(function (a, b) {
      var priceA = a[priceKey] || a.standard || a.fullPrice;
      var priceB = b[priceKey] || b.standard || b.fullPrice;
      return priceA - priceB;
    });
    var best = sorted[0];
    return {
      standard: best.standard,
      member: best.member,
      trade: best.trade,
      label: best.label,
      variantId: best.variantId,
      fullPrice: best.fullPrice,
    };
  }

  /** Search FROM pricing — sweep bridge variant tags with per-variant full price (shared.js). */
  function tagBasedFromBridgeMap(hit, tagCfg, site) {
    var byId = hit._plpVariantPricingById;
    if (!byId || !tagCfg || !tagCfg.enabled) return null;
    var product = hit._shopify;
    var rows = [];
    Object.keys(byId).forEach(function (vid) {
      var row = byId[vid];
      if (!row) return;
      if (row.hiddenOnWeb === true || row.hiddenOnWeb === 'true' || row.hiddenOnWeb === 1) return;
      var pt = row.pricingTag;
      if (!pt) return;
      var vFull = null;
      if (product) {
        var v = findShopifyVariant(product, vid);
        var c = coreFromShopifyVariant(v);
        if (c) vFull = c.fullPrice;
      }
      if (vFull == null) vFull = listPriceFromHit(hit);
      var td = tagDiscountForPricingTag(tagCfg, site, pt, vFull);
      if (!td) return;
      rows.push({
        variantId: vid,
        standard: td.standard,
        member: td.member,
        trade: td.trade,
        label: td.label,
        fullPrice: vFull,
      });
    });
    return tagBasedFromVariantPricingRows(rows, site);
  }

  function variantHiddenOnWeb(v) {
    var vMfields = v.metafields || [];
    for (var i = 0; i < vMfields.length; i++) {
      if (vMfields[i].key === 'hidden_on_web' && vMfields[i].value === 'true') return true;
    }
    return v.mfield_custom_hidden_on_web === 'true';
  }

  function parseSsTagsFromHit(hit) {
    var raw =
      hit.ss_tags ||
      (hit.attributes && hit.attributes.ss_tags) ||
      hit.meta && hit.meta.custom && hit.meta.custom.ss_tags;
    if (raw == null || raw === '') return [];
    if (Array.isArray(raw)) {
      return raw
        .map(function (t) {
          return String(t).trim();
        })
        .filter(Boolean);
    }
    return String(raw)
      .split(/[|,]/)
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  /** Default callout badges — overridden by Theme settings → Algolia PLP badges. */
  var DEFAULT_FEED_BADGE_RULES = [
    { tag: 'afterpay', matchTags: '', label: 'Afterpay It', bg: '#ffffff', color: '#000000', overlay: true, hideWhenTags: '' },
    { tag: 'storage-20-off', matchTags: 'express-sale,EXPRESS-SALE', label: 'EXPRESS SALE', bg: '#ffffff', color: '#000000', overlay: false, hideWhenTags: 'mega-express-sale' },
    { tag: 'in-stock', matchTags: 'ss_available,ss-available', label: 'In Stock', bg: '#ffffff', color: '#000000', overlay: false, hideWhenTags: '' },
    { tag: 'mega-express-sale', matchTags: '', label: 'MEGA EXPRESS SALE', bg: '#e2f098', color: '#000000', overlay: false, hideWhenTags: '' },
  ];

  function splitTagList(raw) {
    if (Array.isArray(raw)) {
      return raw
        .map(function (t) {
          return String(t || '').trim();
        })
        .filter(Boolean);
    }
    return String(raw || '')
      .split(/[|,]/)
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
  }

  function normalizeCalloutRules(rawRules, allowEmpty) {
    if (!Array.isArray(rawRules)) {
      return DEFAULT_FEED_BADGE_RULES.map(function (r) {
        return {
          tag: r.tag,
          matchTags: splitTagList(r.matchTags),
          label: r.label,
          bg: r.bg,
          color: r.color,
          overlay: !!r.overlay,
          side: r.side === 'right' ? 'right' : 'left',
          hideWhenTags: splitTagList(r.hideWhenTags),
        };
      });
    }
    var out = [];
    for (var i = 0; i < rawRules.length; i++) {
      var r = rawRules[i] || {};
      var tag = String(r.tag || '').trim();
      if (!tag) continue;
      out.push({
        tag: tag,
        matchTags: splitTagList(r.matchTags),
        label: String(r.label != null ? r.label : tag),
        bg: r.bg || '#ffffff',
        color: r.color || '#000000',
        overlay: !!r.overlay,
        side: r.side === 'right' ? 'right' : 'left',
        hideWhenTags: splitTagList(r.hideWhenTags),
      });
    }
    if (out.length || allowEmpty) return out;
    return normalizeCalloutRules(null, false);
  }

  function resolveCalloutConfig(ctx) {
    ctx = ctx || {};
    var cfg = ctx.calloutBadgeConfig;
    var hasConfig = cfg && typeof cfg === 'object' && Array.isArray(cfg.rules);
    var rules = normalizeCalloutRules(hasConfig ? cfg.rules : ctx.calloutBadgeRules || null, hasConfig);
    var limit = parseInt(
      hasConfig && cfg.footerLimit != null
        ? cfg.footerLimit
        : ctx.calloutBadgeFooterLimit,
      10
    );
    if (!(limit >= 1 && limit <= 6)) limit = 2;
    return { rules: rules, footerLimit: limit };
  }

  function tagListIncludes(productTags, candidates) {
    for (var i = 0; i < productTags.length; i++) {
      var t = String(productTags[i] || '')
        .toLowerCase()
        .replace(/_/g, '-');
      for (var c = 0; c < candidates.length; c++) {
        var want = String(candidates[c] || '')
          .toLowerCase()
          .replace(/_/g, '-');
        if (t === want) return true;
      }
    }
    return false;
  }

  function ruleMatchCandidates(rule) {
    var list = [rule.tag].concat(rule.matchTags || []);
    var want = String(rule.tag || '')
      .toLowerCase()
      .replace(/_/g, '-');
    /* Built-in aliases when merchant leaves matchTags blank */
    if (!rule.matchTags || !rule.matchTags.length) {
      if (want === 'storage-20-off') list = list.concat(['express-sale']);
      if (want === 'in-stock') list = list.concat(['ss-available', 'ss_available', 'in-stock']);
      if (want === 'afterpay') list = list.concat(['afterpay']);
    }
    return list;
  }

  function productTagsIncludeFeedBadge(productTags, rule) {
    if (!rule || typeof rule === 'string') {
      /* Legacy string lookup against defaults */
      var legacy = normalizeCalloutRules(DEFAULT_FEED_BADGE_RULES);
      for (var i = 0; i < legacy.length; i++) {
        if (String(legacy[i].tag).toLowerCase().replace(/_/g, '-') === String(rule || '').toLowerCase().replace(/_/g, '-')) {
          return productTagsIncludeFeedBadge(productTags, legacy[i]);
        }
      }
      return tagListIncludes(productTags, [rule]);
    }
    return tagListIncludes(productTags, ruleMatchCandidates(rule));
  }

  function ruleIsHiddenByTags(productTags, rule) {
    if (!rule.hideWhenTags || !rule.hideWhenTags.length) return false;
    return tagListIncludes(productTags, rule.hideWhenTags);
  }

  function buildFeedBadges(productTags, limit, rules) {
    var out = [];
    var calloutRules = (rules || normalizeCalloutRules()).filter(function (r) {
      return !r.overlay;
    });
    var max = limit >= 1 ? limit : 2;
    for (var i = 0; i < calloutRules.length && out.length < max; i++) {
      var rule = calloutRules[i];
      if (!productTagsIncludeFeedBadge(productTags, rule)) continue;
      if (ruleIsHiddenByTags(productTags, rule)) continue;
      out.push({
        label: rule.label,
        color: rule.color || '#000',
        bgColor: rule.bg || '',
        overlay: false,
      });
    }
    return out;
  }

  function findOverlayBadge(productTags, rules, side) {
    var want = side === 'right' ? 'right' : 'left';
    var list = rules || normalizeCalloutRules();
    for (var i = 0; i < list.length; i++) {
      var rule = list[i];
      if (!rule.overlay) continue;
      if ((rule.side || 'left') !== want) continue;
      if (!productTagsIncludeFeedBadge(productTags, rule)) continue;
      if (ruleIsHiddenByTags(productTags, rule)) continue;
      var overlayLabel = String(rule.label || rule.tag || '').trim();
      if (!overlayLabel) continue;
      return {
        label: overlayLabel,
        color: rule.color || '#000',
        bgColor: rule.bg || '',
        overlay: true,
        side: want,
      };
    }
    return null;
  }

  function hasOverlayAfterpay(productTags, rules) {
    return !!findOverlayBadge(productTags, rules);
  }

  function normalizeTags(hit) {
    var tags = (hit._shopify && hit._shopify.tags) || hit.tags || [];
    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map(function (t) {
          return t.trim();
        })
        .filter(Boolean);
    }
    return Array.isArray(tags) ? tags : [];
  }

  /** Callout badges use index/feed tags — not Shopify campaign tags like MEGAEXPRESS26. */
  function tagsForFeedBadges(hit, rules) {
    var tags = parseSsTagsFromHit(hit);
    var configured = Array.isArray(rules)
      ? normalizeCalloutRules(rules, true)
      : normalizeCalloutRules(null, false);
    var watch = {};
    configured.forEach(function (r) {
      ruleMatchCandidates(r).forEach(function (t) {
        watch[String(t || '').toLowerCase().replace(/_/g, '-')] = true;
      });
    });
    if (!tags.length) {
      var shop = normalizeTags(hit);
      for (var i = 0; i < shop.length; i++) {
        var tl = String(shop[i] || '').toLowerCase().replace(/_/g, '-');
        if (watch[tl]) tags.push(shop[i]);
      }
    }
    /* Variant-level stock wins when we pinned a matched / filtered variant. */
    if (hit._matchedVariantAvailable === true) {
      tags = tags.slice();
      tags.push('ss_available');
      tags.push('in-stock');
    } else if (hit._matchedVariantAvailable === false) {
      tags = (tags || []).filter(function (t) {
        var tl = String(t || '').toLowerCase().replace(/_/g, '-');
        return tl !== 'ss-available' && tl !== 'ss_available' && tl !== 'in-stock';
      });
    } else {
      var avail = hit.ss_available != null ? hit.ss_available : hit.attributes && hit.attributes.ss_available;
      if (avail === 1 || avail === '1' || avail === true) {
        tags = tags.slice();
        tags.push('ss_available');
      }
      /* Shopify .js overwrites Algolia tags — restore ss_available captured before overwrite. */
      if (hit._plpHadSsAvailable) {
        tags = tags.slice();
        tags.push('ss_available');
      }
    }
    if (hit._plpHadAfterpay) {
      tags = tags.slice();
      tags.push('afterpay');
    }
    return tags;
  }

  function normalizeQueryText(s) {
    return (
      ' ' +
      String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim() +
      ' '
    );
  }

  function variantOptionValues(v) {
    if (!v) return [];
    var vals = [];
    if (Array.isArray(v.options)) vals = vals.concat(v.options);
    ['option1', 'option2', 'option3'].forEach(function (k) {
      if (v[k]) vals.push(v[k]);
    });
    if (!vals.length && v.title && v.title !== 'Default Title') vals = [v.title];
    var parts = [];
    vals.forEach(function (x) {
      String(x).split(' / ').forEach(function (p) {
        if (parts.indexOf(p) === -1) parts.push(p);
      });
    });
    return parts
      .map(function (x) {
        return normalizeQueryText(x);
      })
      .filter(function (x) {
        return x.trim().length > 0;
      });
  }

  /** True when the search text names an option value (colour / size) that sets this variant apart. */
  function queryNamesVariant(hit, query, variantId) {
    var q = normalizeQueryText(query);
    if (!q.trim()) return false;
    var product = hit && hit._shopify;
    var all = (product && product.variants) || [];
    var target = null;
    for (var i = 0; i < all.length; i++) {
      if (String(all[i].id) === String(variantId)) {
        target = all[i];
        break;
      }
    }
    if (!target) return false;
    var targetVals = variantOptionValues(target);
    var shared = {};
    targetVals.forEach(function (val) {
      var everywhere = all.every(function (v) {
        return variantOptionValues(v).indexOf(val) !== -1;
      });
      if (everywhere) shared[val] = true;
    });
    for (var t = 0; t < targetVals.length; t++) {
      var val = targetVals[t];
      if (shared[val]) continue;
      if (q.indexOf(val) !== -1) return true;
      var words = val.trim().split(' ');
      for (var w = 0; w < words.length; w++) {
        if (words[w].length < 3) continue;
        var word = ' ' + words[w] + ' ';
        var wordShared = all.every(function (v) {
          return variantOptionValues(v).join(' ').indexOf(word) !== -1;
        });
        if (!wordShared && q.indexOf(word) !== -1) return true;
      }
    }
    return false;
  }

  function resolveMatchedVariantId(hit) {
    if (!hit) return null;
    if (hit._matchedVariantId != null && hit._matchedVariantId !== '') return String(hit._matchedVariantId);
    if (hit._plpMatchedVariantId != null && hit._plpMatchedVariantId !== '') return String(hit._plpMatchedVariantId);
    if (hit.variant_id != null && /^\d+$/.test(String(hit.variant_id))) return String(hit.variant_id);
    if (hit.objectID != null && /^\d+$/.test(String(hit.objectID))) return String(hit.objectID);
    return null;
  }

  /**
   * @param {object} hit Algolia record + optional hit._shopify
   * @param {object} ctx { site, tag_based_discount_config, badges, badgeTag, discounts, plpPricingMode }
   *   plpPricingMode: 'lowestList' | 'matchedVariant' | default (best-visible / FROM)
   */
  function enrichHit(hit, ctx) {
    ctx = ctx || {};
    var site = normalizeSiteFlags(ctx.site || { loggedInMember: false, loggedInTrade: false, customerTags: [] });
    var tagCfg = ctx.tag_based_discount_config;
    var badgeTag = ctx.badgeTag || [];
    var badgesCfg = ctx.badges || {};
    var discounts = ctx.discounts || {};
    var lowestListOnly = ctx.plpPricingMode === 'lowestList';
    var matchedVariantId = resolveMatchedVariantId(hit);
    var matchedMode = ctx.plpPricingMode === 'matchedVariant' && !!matchedVariantId;
    if (matchedMode && typeof ctx.searchQuery === 'string' && !queryNamesVariant(hit, ctx.searchQuery, matchedVariantId)) {
      matchedMode = false;
    }

    var variants = [];
    /* Prefer Shopify product.js variants when present — Algolia hits are often one indexed variant. */
    if (hit._shopify) {
      variants = shopifyVariantsAsSnapVariants(hit._shopify);
    }
    if (!variants.length) {
      variants = parseVariantsFromHit(hit);
    }
    applyBridgeMetafieldsToVariants(hit, variants);

    var visibleVariants = variants.filter(function (v) {
      return !variantHiddenOnWeb(v);
    });

    /* Search: price the query-matched variant (3-seater / blue), not product FROM/min. */
    var matchedSnapVariant = null;
    if (matchedMode && matchedVariantId) {
      for (var mvi = 0; mvi < visibleVariants.length; mvi++) {
        if (String(visibleVariants[mvi].id) === String(matchedVariantId)) {
          matchedSnapVariant = visibleVariants[mvi];
          break;
        }
      }
      if (!matchedSnapVariant) {
        for (var mva = 0; mva < variants.length; mva++) {
          if (String(variants[mva].id) === String(matchedVariantId)) {
            matchedSnapVariant = variants[mva];
            break;
          }
        }
      }
    }

    var listFromHit = listPriceFromHit(hit);
    var compareFromHit = compareAtFromHit(hit);
    if (matchedSnapVariant) {
      var matchedList = parseFloat(matchedSnapVariant.price);
      var matchedCompare = parseFloat(matchedSnapVariant.compare_at_price || matchedSnapVariant.price);
      if (!isNaN(matchedList) && matchedList > 0) listFromHit = matchedList;
      if (!isNaN(matchedCompare) && matchedCompare > 0) compareFromHit = matchedCompare;
    }
    var core = {
      price: listFromHit,
      msrp: compareFromHit != null && compareFromHit > listFromHit ? compareFromHit : listFromHit,
    };

    var bestVisibleMember = null;
    var bestVisibleTrade = null;
    var memberPrices = [];
    var tradePrices = [];

    var memberTradeSource = matchedSnapVariant ? [matchedSnapVariant] : visibleVariants;
    memberTradeSource.forEach(function (v) {
      var refDollars = parseFloat(v.price);
      if (isNaN(refDollars) || refDollars <= 0) refDollars = core.price;
      var mMember = getMfieldMoney(v, 'member_pricing');
      var mTrade = getMfieldMoney(v, 'trade_pricing');
      if (mMember !== null) {
        mMember = normalizePlpMetafieldMoney(mMember, refDollars) || mMember;
      }
      if (mTrade !== null) {
        mTrade = normalizePlpMetafieldMoney(mTrade, refDollars) || mTrade;
      }
      if (mMember !== null) {
        memberPrices.push(mMember);
        if (!bestVisibleMember || mMember < bestVisibleMember.price) {
          bestVisibleMember = { price: mMember, variantId: v.id };
        }
      }
      if (mTrade !== null) {
        tradePrices.push(mTrade);
        if (!bestVisibleTrade || mTrade < bestVisibleTrade.price) {
          bestVisibleTrade = { price: mTrade, variantId: v.id };
        }
      }
    });

    var hasMemberRange =
      !matchedMode &&
      memberPrices.length > 1 &&
      Math.max.apply(null, memberPrices) !== Math.min.apply(null, memberPrices);
    var hasTradeRange =
      !matchedMode &&
      tradePrices.length > 1 &&
      Math.max.apply(null, tradePrices) !== Math.min.apply(null, tradePrices);

    var lowestListVariant = null;
    if (matchedSnapVariant) {
      lowestListVariant = matchedSnapVariant;
      /* Keep matched variant list price — do not rewrite to product min/FROM. */
    } else if (visibleVariants.length > 0) {
      var sorted = visibleVariants.slice().sort(function (a, b) {
        return parseFloat(a.price) - parseFloat(b.price);
      });
      var lowest = sorted[0];
      lowestListVariant = lowest;
      var lowestPrice = parseFloat(lowest.price);
      if (!isNaN(lowestPrice)) {
        if (core.price < lowestPrice) {
          core.price = lowestPrice;
          core.msrp = parseFloat(lowest.compare_at_price || lowest.price);
        }
        /* Algolia index: `price` can exceed visible minimum; SS feed core.price is already the PLP min. */
        if (core.price > lowestPrice) {
          core.price = lowestPrice;
          core.msrp = parseFloat(lowest.compare_at_price || lowest.price) || core.msrp;
        }
      }
    }
    if (lowestListOnly && lowestListVariant && lowestListVariant.id != null) {
      hit._plpLowestListVariantId = String(lowestListVariant.id);
    }

    if (isNaN(core.price)) core.price = 0;
    if (isNaN(core.msrp)) core.msrp = core.price;

    var hasPrice = core.price !== 0;
    var hasSale = core.msrp > core.price;
    var fullPrice = core.msrp > core.price ? core.msrp : core.price;
    var hasRange = false;
    if (!matchedMode && visibleVariants.length > 1) {
      var prices = visibleVariants.map(function (v) {
        return parseFloat(v.price);
      });
      hasRange = Math.max.apply(null, prices) !== Math.min.apply(null, prices);
    }

    var tagBasedDiscount = null;
    var variantPricing = [];

    if (!lowestListOnly && tagCfg && tagCfg.enabled && tagCfg.rules && variants.length) {
      variants.forEach(function (variant) {
        if (variantHiddenOnWeb(variant)) return;

        var pricingTag = getMfieldRaw(variant, 'pricing_tag');
        if (typeof pricingTag === 'string' && pricingTag.indexOf('[') === 0) {
          try {
            pricingTag = JSON.parse(pricingTag);
          } catch (e) {}
        }
        var pricingTags = Array.isArray(pricingTag) ? pricingTag : pricingTag ? [pricingTag] : [];

        var variantPrice = parseFloat(variant.price);
        var variantMsrp = parseFloat(variant.compare_at_price || variant.price);
        var vFullPrice = variantMsrp > variantPrice ? variantMsrp : variantPrice;

        if (pricingTags.length > 0) {
          var activeRule = null;
          for (var ri = 0; ri < tagCfg.rules.length; ri++) {
            var rule = tagCfg.rules[ri];
            if (!rule.enabled) continue;
            for (var pt = 0; pt < pricingTags.length; pt++) {
              if (String(pricingTags[pt]).toLowerCase() === String(rule.pricingTag).toLowerCase()) {
                activeRule = rule;
                break;
              }
            }
            if (activeRule) break;
          }

          if (activeRule) {
            function getPriceForRule(customerTag) {
              for (var rj = 0; rj < tagCfg.rules.length; rj++) {
                var r = tagCfg.rules[rj];
                if (!r.enabled || r.pricingTag !== activeRule.pricingTag) continue;
                if (String(r.customerTag || '') !== String(customerTag || '')) continue;
                if (r.excludedCustomerTags && r.excludedCustomerTags.length) {
                  var excluded = false;
                  for (var ex = 0; ex < r.excludedCustomerTags.length; ex++) {
                    if ((site.customerTags || []).indexOf(r.excludedCustomerTags[ex]) !== -1) excluded = true;
                  }
                  if (excluded) return null;
                }
                if (r.discountType === 'percentage') {
                  return calculateFloat((vFullPrice * (100 - Number(r.discountValue))) / 100);
                }
              }
              return null;
            }

            var standard = getPriceForRule('');
            var member = getPriceForRule('member');
            var trade = getPriceForRule('trade');
            if (standard || member || trade) {
              variantPricing.push({
                variantId: variant.id,
                standard: standard,
                member: member,
                trade: trade,
                label: activeRule.label,
                fullPrice: vFullPrice,
              });
            }
          }
        }
      });

      if (variantPricing.length > 0) {
        var priceKey = site.loggedInTrade ? 'trade' : 'member';
        var sortedPricing = variantPricing.slice().sort(function (a, b) {
          var priceA = a[priceKey] || a.standard || a.fullPrice;
          var priceB = b[priceKey] || b.standard || b.fullPrice;
          return priceA - priceB;
        });
        var bestVariant = sortedPricing[0];
        if (matchedMode && matchedVariantId) {
          for (var mvp = 0; mvp < variantPricing.length; mvp++) {
            if (String(variantPricing[mvp].variantId) === String(matchedVariantId)) {
              bestVariant = variantPricing[mvp];
              break;
            }
          }
        }
        tagBasedDiscount = {
          standard: bestVariant.standard,
          member: bestVariant.member,
          trade: bestVariant.trade,
          label: bestVariant.label,
          variantId: bestVariant.variantId,
          fullPrice: bestVariant.fullPrice,
        };
        var rangeKey = site.loggedInTrade ? 'trade' : 'member';
        var tagPrices = variantPricing
          .map(function (vp) {
            return vp[rangeKey] || vp.standard;
          })
          .filter(function (p) {
            return p !== null && p !== undefined && Number(p) > 0;
          });
        /* "from" follows that shopper's prices. Logged-in member/trade do not inherit a retail range. */
        if (site.loggedInMember || site.loggedInTrade) {
          hasRange =
            !matchedMode &&
            tagPrices.length > 1 &&
            Math.max.apply(null, tagPrices) !== Math.min.apply(null, tagPrices);
        } else if (!matchedMode && tagPrices.length > 1 && Math.max.apply(null, tagPrices) !== Math.min.apply(null, tagPrices)) {
          hasRange = true;
        }
      }
    }

    /* Bridge-only fallback when variants lack pricing_tag until injection (search PLP). */
    if (!lowestListOnly && !tagBasedDiscount) {
      tagBasedDiscount = tagBasedFromBridgeMap(hit, tagCfg, site);
    }

    var productTags = normalizeTags(hit);
    var memberPrice = null;
    var memberPricePrefix = null;
    var hasMemberPromo = false;
    var hasTradePromo = false;

    /* shared.js — never pin core to Liquid selected_or_first_available_variant */

    var expressProduct = isExpressProductTags(productTags) || !!tagBasedDiscount;

    if (!lowestListOnly && tagBasedDiscount) {
      if (site.loggedInTrade) {
        memberPrice = tagBasedDiscount.trade;
        memberPricePrefix = 'Trade';
        hasTradePromo = true;
      } else {
        /* Guest + member both use tag rule member % (e.g. Express30 → 30%). */
        memberPrice = tagBasedDiscount.member;
        memberPricePrefix = 'Member';
        hasMemberPromo = true;
      }
      /* Compare against the tagged variant's full price. A cheaper variant
         without the sale tag must not hide Active Last Dance pricing. */
      var tagFull = Number(tagBasedDiscount.fullPrice);
      var compareFull = tagFull > 0 ? tagFull : fullPrice;
      if (!memberPrice || memberPrice <= 0 || memberPrice >= compareFull) {
        tagBasedDiscount = null;
        memberPrice = null;
        memberPricePrefix = null;
        hasTradePromo = false;
        hasMemberPromo = false;
      } else if (tagFull > 0) {
        fullPrice = tagFull;
      }
    }

    if (!lowestListOnly && !tagBasedDiscount && discounts) {
      var memberPct = discounts.member;
      if (memberPct != null && memberPct !== '') {
        memberPrice = calculateFloat((core.price * (100 - parseFloat(memberPct))) / 100);
        memberPricePrefix = 'Member';
      }
      if (bestVisibleMember) {
        memberPrice = bestVisibleMember.price;
        memberPricePrefix = 'Member';
        hasMemberPromo = true;
        if (site.loggedInMember && !site.loggedInTrade) hasRange = !!hasMemberRange;
        else if (hasMemberRange) hasRange = true;
      }
      if (bestVisibleTrade && site.loggedInTrade) {
        memberPrice = bestVisibleTrade.price;
        memberPricePrefix = 'Trade';
        hasTradePromo = true;
        hasRange = !!hasTradeRange;
      }
    }

    if (!lowestListOnly && (!memberPrice || memberPrice <= 0)) {
      memberPrice = null;
      memberPricePrefix = null;
      hasMemberPromo = false;
      hasTradePromo = false;
    }

    if (lowestListOnly) {
      tagBasedDiscount = null;
      hasSale = core.msrp > core.price;
      fullPrice = core.msrp > core.price ? core.msrp : core.price;
      memberPrice = null;
      memberPricePrefix = null;
      hasMemberPromo = false;
      hasTradePromo = false;

      var lowestVid = hit._plpLowestListVariantId;
      var bridgeRow =
        lowestVid && hit._plpVariantPricingById ? hit._plpVariantPricingById[String(lowestVid)] : null;

      tagBasedDiscount = tagBasedFromBridgeMap(hit, tagCfg, site);
      if (!tagBasedDiscount && tagCfg && tagCfg.enabled) {
        var lowestTag =
          bridgeRow && bridgeRow.pricingTag != null && bridgeRow.pricingTag !== '' ? bridgeRow.pricingTag : null;
        if (!lowestTag && hit._plpPricingTag) lowestTag = hit._plpPricingTag;
        if (!lowestTag) lowestTag = inferPricingTagFromTags(productTags);
        if (lowestTag) {
          tagBasedDiscount = tagDiscountForPricingTag(tagCfg, site, lowestTag, fullPrice);
          if (tagBasedDiscount && lowestVid) tagBasedDiscount.variantId = lowestVid;
        }
      }
      if (tagBasedDiscount) {
        if (site.loggedInTrade) {
          memberPrice = tagBasedDiscount.trade;
          memberPricePrefix = 'Trade';
          hasTradePromo = true;
        } else {
          memberPrice = tagBasedDiscount.member;
          memberPricePrefix = 'Member';
          hasMemberPromo = true;
        }
        var tagFull = Number(tagBasedDiscount.fullPrice);
        var compareFull = tagFull > 0 ? tagFull : fullPrice;
        if (!memberPrice || memberPrice <= 0 || memberPrice >= compareFull) {
          tagBasedDiscount = null;
          memberPrice = null;
          memberPricePrefix = null;
          hasMemberPromo = false;
          hasTradePromo = false;
        } else {
          if (tagFull > 0) fullPrice = tagFull;
          hasSale = true;
        }
      }

      if (!tagBasedDiscount && discounts) {
        /* SS: member_pricing only — ignore memberPromoPricing */
        var bridgeMember = bridgeRow ? normalizePlpMetafieldMoney(bridgeRow.memberPricing, core.price) : null;
        if (bridgeMember != null && bridgeMember > 0) {
          memberPrice = bridgeMember;
        } else if (lowestListVariant) {
          var mfMember = getMfieldMoney(lowestListVariant, 'member_pricing');
          if (mfMember != null && mfMember > 0) memberPrice = mfMember;
        }
        if (memberPrice == null && discounts.member != null && discounts.member !== '') {
          memberPrice = calculateFloat((core.price * (100 - parseFloat(discounts.member))) / 100);
        }
        if (memberPrice != null && memberPrice > 0 && memberPrice < fullPrice) {
          memberPricePrefix = 'Member';
          hasMemberPromo = !!(bridgeMember != null || (lowestListVariant && getMfieldMoney(lowestListVariant, 'member_pricing') != null));
        } else {
          memberPrice = null;
          memberPricePrefix = null;
          hasMemberPromo = false;
        }
      }
    } else {
      hasSale = tagBasedDiscount ? true : core.msrp > core.price;
      var keptTagFull = tagBasedDiscount ? Number(tagBasedDiscount.fullPrice) : 0;
      if (keptTagFull > 0) fullPrice = keptTagFull;
      else fullPrice = core.msrp > core.price ? core.msrp : core.price;
    }
    hasPrice = core.price !== 0;

    var displayVariantId = null;
    if (matchedMode && matchedVariantId) {
      displayVariantId = String(matchedVariantId);
    } else if (tagBasedDiscount && tagBasedDiscount.variantId != null) {
      displayVariantId = String(tagBasedDiscount.variantId);
    } else if (site.loggedInTrade && bestVisibleTrade && bestVisibleTrade.variantId != null) {
      displayVariantId = String(bestVisibleTrade.variantId);
    } else if (bestVisibleMember && bestVisibleMember.variantId != null) {
      displayVariantId = String(bestVisibleMember.variantId);
    } else if (lowestListVariant && lowestListVariant.id != null) {
      displayVariantId = String(lowestListVariant.id);
    }
    if (displayVariantId) hit._plpDisplayVariantId = displayVariantId;
    if (matchedMode && matchedVariantId) hit._matchedVariantId = String(matchedVariantId);

    /* Search Spring shared.js — thumb badges use final hasSale / memberPrice / core */
    var customBadges = [];

    if (badgeTag && badgeTag.length) {
      var texts = badgesCfg.text || [];
      var colors = badgesCfg.color || [];
      var bgs = badgesCfg.bgColor || badgesCfg.background || [];
      var checks = badgesCfg.check || [];
      badgeTag.forEach(function (tag, index) {
        var found = false;
        for (var ti = 0; ti < productTags.length; ti++) {
          if (productTags[ti] === tag) found = true;
        }
        if (!found) return;
        var badgeLabel = texts[index] || '';
        var rawTemplate = texts[index] || '';
        var usesDiscountBadge = false;
        if (badgeLabel.indexOf('discount') !== -1 && hasSale) {
          var basePrice = core.msrp > core.price ? core.msrp : core.price;
          var currentPrice = memberPrice || core.price;
          var discountPct = Math.round(((basePrice - currentPrice) / basePrice) * 100);
          badgeLabel = badgeLabel.replace('%discount', String(discountPct));
          usesDiscountBadge = discountPct > 0;
        }
        customBadges.push({
          label: badgeLabel,
          text: rawTemplate,
          color: colors[index] || '',
          bgColor: bgs[index] || '',
          check: checks[index] || '',
          usesDiscount: usesDiscountBadge,
        });
      });
    }

    if (tagBasedDiscount && tagBasedDiscount.label) {
      customBadges.push({ label: tagBasedDiscount.label, color: '#fff', bgColor: '#000' });
    }

    /* Lowest public sale "From" across variants — not the full/compare price. */
    var saleFromPrice = null;
    if (variantPricing.length) {
      var saleFromPrices = variantPricing
        .map(function (vp) {
          return Number(vp.standard);
        })
        .filter(function (amount) {
          return amount > 0;
        });
      if (saleFromPrices.length) saleFromPrice = Math.min.apply(null, saleFromPrices);
    }
    if ((saleFromPrice == null || !(saleFromPrice > 0)) && hasSale && core.price > 0) {
      saleFromPrice = core.price;
    }

    var calloutCfg = resolveCalloutConfig(ctx);
    var badgeTags = tagsForFeedBadges(hit, calloutCfg.rules);
    var feedCallouts = buildFeedBadges(badgeTags, calloutCfg.footerLimit, calloutCfg.rules);
    var overlayBadge = findOverlayBadge(badgeTags, calloutCfg.rules, 'left');
    var overlayBadgeRight = findOverlayBadge(badgeTags, calloutCfg.rules, 'right');
    var overlayAfterpay = !!overlayBadge;

    hit._snap = {
      hasPrice: hasPrice,
      hasSale: tagBasedDiscount ? true : hasSale,
      hasRange: hasRange,
      hasMemberPromo: hasMemberPromo,
      hasTradePromo: hasTradePromo,
      memberPrice: memberPrice,
      memberPricePrefix: memberPricePrefix,
      tagBased: tagBasedDiscount,
      fullPrice: fullPrice,
      corePrice: core.price,
      coreMsrp: core.msrp,
      saleFromPrice: saleFromPrice,
      isExpressProduct: expressProduct,
      badges: customBadges,
      calloutBadges: feedCallouts,
      overlayAfterpay: overlayAfterpay,
      overlayBadge: overlayBadge,
      overlayBadgeRight: overlayBadgeRight,
      displayVariantId: displayVariantId,
    };

    return hit;
  }

  /** Search Spring/src/shared/pricing/Pricing.jsx (PLP markup only) — match live Snap DOM. */
  function renderPricingHtml(hit, opts) {
    opts = opts || {};
    var custom = hit._snap;
    if (!custom || !custom.hasPrice) return '';
    var site = normalizeSiteFlags(opts.site || { loggedInMember: false, loggedInTrade: false });
    var formatMoney = opts.formatMoney;
    var escapeHtml = opts.escapeHtml || function (s) {
      return String(s);
    };
    if (typeof formatMoney !== 'function') return '';

    var corePrice = custom.corePrice;
    var coreMsrp = custom.coreMsrp != null ? custom.coreMsrp : corePrice;
    var rangeFrom = custom.hasRange ? ' from' : '';

    var wrapClass =
      'ss__result__pricing o-product-pricing o-type--3 o-product-thumbnail__pricing' +
      (custom.hasSale || custom.memberPrice || custom.tagBased ? ' has-compare' : '') +
      (custom.memberPrice ? ' ss__result__pricing--member-sale on-sale-price' : '') +
      (custom.tagBased ? ' ss__result__pricing--tag-based' : '') +
      (site.loggedInMember || site.loggedInTrade ? ' ss__result__pricing--member-auth' : '');

    var html = '<p class="' + escapeHtml(wrapClass) + '" data-product-pricing>';

    if (custom.tagBased) {
      html +=
        '<span class="ss__result__price o-product-pricing__price ss__result__price--on-sale ss__result__price--has-group-sale">';
      html +=
        '<span class="text-no-wrap">' +
        escapeHtml(custom.memberPricePrefix + ' sale price' + rangeFrom + ': ') +
        escapeHtml(formatMoney(custom.memberPrice));
      if (!site.loggedInMember && !site.loggedInTrade && custom.tagBased.standard > 0) {
        html +=
          '</span><span class="ss__result__price-sep"> / </span><span class="ss__result__price--sale text-no-wrap">Sale price: ' +
          escapeHtml(formatMoney(custom.tagBased.standard)) +
          '</span>';
      } else {
        html += '</span>';
      }
      html += '</span>';
      var strikeFull =
        !site.loggedInMember && !site.loggedInTrade && !(custom.tagBased.standard > 0)
          ? ' ss__result__msrp--no-strike'
          : ' ss__result__msrp--strike';
      html +=
        '<span class="ss__result__msrp text-no-wrap' +
        strikeFull +
        '">' + escapeHtml('Full price' + rangeFrom + ': ') +
        escapeHtml(formatMoney(custom.fullPrice)) +
        '</span>';
    } else if (custom.memberPrice && custom.memberPrice > 0) {
      html +=
        '<span class="ss__result__price o-product-pricing__price' +
        (custom.hasSale || custom.hasMemberPromo || custom.hasTradePromo ? ' ss__result__price--on-sale' : '') +
        (custom.hasSale ? ' ss__result__price--has-group-sale' : '') +
        '">';
      if (custom.hasMemberPromo || custom.hasTradePromo) {
        html +=
          '<span class="text-no-wrap">' +
          escapeHtml(custom.memberPricePrefix + ' price' + rangeFrom + ': ') +
          escapeHtml(formatMoney(custom.memberPrice));
        if (custom.hasSale) {
          html +=
            '</span><span class="ss__result__price-sep"> / </span><span class="ss__result__price--sale text-no-wrap">Sale price: ' +
            escapeHtml(formatMoney(corePrice)) +
            '</span>';
        } else {
          html += '</span>';
        }
      } else {
        html +=
          '<span class="text-no-wrap">' +
          escapeHtml(custom.memberPricePrefix + ' price' + rangeFrom + ': ') +
          escapeHtml(formatMoney(custom.memberPrice)) +
          '</span>';
      }
      html += '</span>';
      html +=
        '<span class="ss__result__msrp text-no-wrap' +
        (custom.hasSale ? ' ss__result__msrp--strike' : '') +
        '">' + escapeHtml('Full price' + rangeFrom + ': ') +
        escapeHtml(formatMoney(custom.fullPrice)) +
        '</span>';
    } else {
      html +=
        '<span class="ss__result__price o-product-pricing__price' +
        (custom.hasSale ? ' ss__result__price--on-sale' : '') +
        '">';
      if (custom.hasRange) {
        html += '<span class="o-product-pricing__price-varies-text">From&nbsp;</span>';
      }
      html += '<span class="text-no-wrap">' + escapeHtml(formatMoney(corePrice)) + '</span>';
      html += '</span>';
      if (custom.hasSale) {
        html +=
          '<span class="ss__result__msrp ss__result__msrp--strike o-product-pricing__compare-price text-no-wrap">' + escapeHtml('Full price' + rangeFrom + ': ') +
          escapeHtml(formatMoney(coreMsrp)) +
          '</span>';
      }
    }

    html += '</p>';
    return html;
  }

  global.McmAlgoliaSnapParity = {
    enrichHit: enrichHit,
    renderPricingHtml: renderPricingHtml,
    queryNamesVariant: queryNamesVariant,
    isExpressProductTags: isExpressProductTags,
    inferPricingTagFromTags: inferPricingTagFromTags,
  };
})(typeof window !== 'undefined' ? window : this);
